/**
 * Stable, native date formatting using Intl.DateTimeFormat (no string tokens).
 * Returns a localized string.
 *
 * Defaults:
 * - locale: 'de-DE'
 * - timeZone: 'Europe/Berlin'
 *
 * Uses Laravel-/Carbon-like preset tokens for readability.
 */

export type DatePreset =
    | "d.m."        // 17.10.
    | "d.m.Y"       // 17.10.2025
    | "D"           // Fr
    | "l"           // Freitag
    | "D, d.m."     // Fr, 17.10.
    | "l, d.m."     // Freitag, 17.10.
    | "d. M."       // 17. Okt.
    | "d. F"        // 17. Oktober
    | "d. M Y"      // 17. Okt. 2025
    | "d. F Y"      // 17. Oktober 2025
    | "M Y"         // Okt. 2025
    | "F Y";        // Oktober 2025

export interface FormatDateOptions {
    locale?: string;                    // default 'de-DE'
    timeZone?: string;                  // default 'Europe/Berlin'
    preset?: DatePreset;                // convenience preset
    intl?: Intl.DateTimeFormatOptions;  // or custom Intl options
}

export const defaultLocale = "de-DE";
export const defaultTimeZone = "Europe/Berlin";

/** Map token-style presets to Intl configuration */
export function applyPreset(
    date: Date,
    preset: DatePreset,
    locale: string,
    timeZone: string
): string {
    switch (preset) {
        case "d.m.": {
            const d = new Intl.DateTimeFormat(locale, {
                day: "2-digit",
                month: "2-digit",
                timeZone,
            }).format(date);
            return d.endsWith(".") ? d : `${d}.`;
        }
        case "d.m.Y": {
            return new Intl.DateTimeFormat(locale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                timeZone,
            }).format(date);
        }
        case "D": {
            return new Intl.DateTimeFormat(locale, {
                weekday: "short",
                timeZone,
            }).format(date);
        }
        case "l": {
            return new Intl.DateTimeFormat(locale, {
                weekday: "long",
                timeZone,
            }).format(date);
        }
        case "D, d.m.": {
            const wd = applyPreset(date, "D", locale, timeZone);
            const dm = applyPreset(date, "d.m.", locale, timeZone);
            return `${wd}, ${dm}`;
        }
        case "l, d.m.": {
            const wd = applyPreset(date, "l", locale, timeZone);
            const dm = applyPreset(date, "d.m.", locale, timeZone);
            return `${wd}, ${dm}`;
        }
        case "d. M.": {
            return new Intl.DateTimeFormat(locale, {
                day: "2-digit",
                month: "short",
                timeZone,
            }).format(date);
        }
        case "d. F": {
            return new Intl.DateTimeFormat(locale, {
                day: "2-digit",
                month: "long",
                timeZone,
            }).format(date);
        }
        case "d. M Y": {
            return new Intl.DateTimeFormat(locale, {
                day: "2-digit",
                month: "short",
                year: "numeric",
                timeZone,
            }).format(date);
        }
        case "d. F Y": {
            return new Intl.DateTimeFormat(locale, {
                day: "2-digit",
                month: "long",
                year: "numeric",
                timeZone,
            }).format(date);
        }
        case "M Y": {
            return new Intl.DateTimeFormat(locale, {
                month: "short",
                year: "numeric",
                timeZone,
            }).format(date);
        }
        case "F Y": {
            return new Intl.DateTimeFormat(locale, {
                month: "long",
                year: "numeric",
                timeZone,
            }).format(date);
        }
    }
}

/** Format date as string or with custom Intl options */
export function formatDate(date: Date, opts: FormatDateOptions = {}): string {
    const locale = opts.locale ?? defaultLocale;
    const timeZone = opts.timeZone ?? defaultTimeZone;

    if (opts.preset) {
        return applyPreset(date, opts.preset, locale, timeZone);
    }

    const intlOpts: Intl.DateTimeFormatOptions =
        opts.intl ?? { day: "2-digit", month: "2-digit", year: "numeric", timeZone };

    return new Intl.DateTimeFormat(locale, intlOpts).format(date);
}
